import * as React from 'react';
import { memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { PopupContainer, PopupErrors, TeacherAddFields } from 'molecules';
import { Button, Form, ISelectOption } from 'atoms';

import { ErrorsType } from 'types';

import { ITeacherAddFieldsValues } from '../TeacherAddPopup/TeacherAddFields';

export interface ITeacherQuickAddPopup {
  values?: ITeacherAddFieldsValues | null;
  errors?: ErrorsType<ITeacherAddFieldsValues> | null;
  errorMessage?: string | null;
  loading?: boolean;
  disabledButton: boolean;
  className?: string;
  onInputChange: (
    name: string,
    value?: string | boolean | ISelectOption<string>[],
  ) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  onClose: () => void;
}

/**
 * Lean «свободный»-teacher creation popup (spec 0007 rev.): the base teacher
 * fields only, no application / employment-type step. Reuses `TeacherAddFields`
 * so validation and layout stay identical to the full add flow.
 */
export const TeacherQuickAddPopupComponent = memo(
  ({
    values,
    errors,
    errorMessage,
    onInputChange,
    handleSubmit,
    onClose,
    loading,
    className,
    disabledButton,
  }: ITeacherQuickAddPopup) => {
    const { t } = useTranslation();
    return (
      <PopupContainer
        title={t('teacherPopup.quickAdd.title')}
        onClose={onClose}
      >
        <FormWrapper onSubmit={handleSubmit} className={className}>
          <TeacherAddFields
            values={values}
            errors={errors}
            onInputChange={onInputChange}
          />
          <SubmitButton
            type="submit"
            variant="primary"
            colors={{
              color: 'var(--k-fg-ffffff)',
              activeColor: 'var(--k-fg-0d53fc)',
              backgroundColor: 'var(--k-bg-0d53fc)',
              bgDisabledColor: 'var(--k-bg-c6ccd0)',
            }}
            loading={loading}
            disabled={disabledButton || loading}
          >
            {t('teacherPopup.quickAdd.submit')}
          </SubmitButton>
        </FormWrapper>
        <PopupErrorsStyled>{errorMessage}</PopupErrorsStyled>
      </PopupContainer>
    );
  },
);

const FormWrapper = styled(Form)`
  margin-top: 32px;
`;

const SubmitButton = styled(Button)`
  width: 258px;
  margin: 32px 0 0 auto;
`;

const PopupErrorsStyled = styled(PopupErrors)`
  margin-top: 24px;
`;
